import React, { useContext, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import AppContext from "../../context/AppContext";
import { cn } from "../../lib/utils";

const getDefaultDescription = (title) => {
  if (!title) return "";
  const t = title.toLowerCase().trim();
  if (t === "sign in" || t === "login" || t === "log in") {
    return "Enter your credentials to securely access your account dashboard.";
  }
  if (t === "sign out" || t === "logout" || t === "log out") {
    return "Safely end your session and log out of the application.";
  }
  if (t === "save" || t === "save changes" || t === "submit") {
    return "Commit your configurations and sync them with the server.";
  }
  if (t === "cancel" || t === "go back" || t === "discard") {
    return "Exit this view and discard any unsaved modifications.";
  }
  if (t === "delete" || t === "remove") {
    return "Permanently delete this item from the database. This cannot be undone.";
  }
  if (t === "edit" || t === "update") {
    return "Modify properties and details of this entry.";
  }
  if (t === "download" || t === "export" || t === "export data") {
    return "Compile and download these records as a local file.";
  }
  if (t === "add" || t === "create" || t === "new") {
    return "Create a brand new record in the current workspace.";
  }
  if (t === "search" || t === "find") {
    return "Filter entries based on your input keyword.";
  }
  return "";
};

const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 8;
// Horizontal padding (px-3) and border of the bubble, added to the measured text width.
const BUBBLE_EXTRA_WIDTH = 24 + 2 + 1;
const OPPOSITE_POSITION = { top: "bottom", bottom: "top", left: "right", right: "left" };

const CommonTooltip = ({
  children,
  text,
  description,
  shortcut,
  position = "top",
  theme,
  className = "",
  // Extra classes for the bubble itself (e.g. a wider max-width); merged after the defaults.
  tooltipClassName = "",
  disableDefaultDescription = false,
  // Shrinks a text-only bubble to its widest wrapped line, so a message that wraps has no
  // empty space on the right.
  fitContent = false,
  // Keeps the bubble inside the window horizontally; the arrow keeps pointing at the trigger.
  clampToViewport = false,
  // Opens on mouse hover only, not when the trigger gets keyboard/click focus.
  hoverOnly = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // null until measured, so the tooltip never paints at a stale spot first.
  const [coords, setCoords] = useState(null);
  const [placement, setPlacement] = useState(position);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const textRef = useRef(null);
  const [fitWidth, setFitWidth] = useState(null);
  const [shiftX, setShiftX] = useState(0);
  const { langMode, theme: contextTheme } = useContext(AppContext);
  const effectiveTheme = theme || contextTheme;

  const finalDescription =
    description || (disableDefaultDescription ? "" : getDefaultDescription(text));

  // The requested side is a preference: when the tooltip doesn't fit there it opens on the
  // opposite side instead of being cut off by the window edge.
  const resolvePlacement = (triggerRect, tooltipRect) => {
    let preferred = position;
    if (langMode === "ar") {
      if (position === "left") preferred = "right";
      else if (position === "right") preferred = "left";
    }
    if (!tooltipRect) return preferred;

    const fits = {
      top: triggerRect.top - TOOLTIP_GAP - tooltipRect.height >= 0,
      bottom: triggerRect.bottom + TOOLTIP_GAP + tooltipRect.height <= window.innerHeight,
      left: triggerRect.left - TOOLTIP_GAP - tooltipRect.width >= 0,
      right: triggerRect.right + TOOLTIP_GAP + tooltipRect.width <= window.innerWidth,
    };

    if (fits[preferred]) return preferred;
    const opposite = OPPOSITE_POSITION[preferred] || "top";
    return fits[opposite] ? opposite : preferred;
  };

  const getCoords = (nextPlacement, rect) => {
    if (nextPlacement === "bottom") {
      return { top: rect.bottom + TOOLTIP_GAP, left: rect.left + rect.width / 2 };
    }
    if (nextPlacement === "left") {
      return { top: rect.top + rect.height / 2, left: rect.left - TOOLTIP_GAP };
    }
    if (nextPlacement === "right") {
      return { top: rect.top + rect.height / 2, left: rect.right + TOOLTIP_GAP };
    }
    return { top: rect.top - TOOLTIP_GAP, left: rect.left + rect.width / 2 };
  };

  // Layout effect, not effect: the tooltip is measured and positioned in the same commit
  // it is added to the DOM, so it appears at the hover point instead of animating in from
  // the corner it would otherwise paint at first.
  useLayoutEffect(() => {
    if (!isVisible) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const nextPlacement = resolvePlacement(
        triggerRect,
        tooltipRef.current?.getBoundingClientRect()
      );
      setPlacement(nextPlacement);
      setCoords(getCoords(nextPlacement, triggerRect));
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, position, langMode, text, finalDescription]);

  // Measured at the natural (wrapped) width, then pinned to the widest line, so the lines
  // keep their breaks.
  useLayoutEffect(() => {
    if (!isVisible || !fitContent || finalDescription) {
      setFitWidth(null);
      return;
    }
    const element = textRef.current;
    if (!element || typeof document.createRange !== "function") return;
    try {
      const range = document.createRange();
      range.selectNodeContents(element);
      const rect = range.getBoundingClientRect?.();
      if (rect && rect.width > 0) setFitWidth(Math.ceil(rect.width) + BUBBLE_EXTRA_WIDTH);
    } catch {
      setFitWidth(null);
    }
  }, [isVisible, fitContent, text, finalDescription]);

  // Top/bottom bubbles are centred on the trigger; move them in when they would cross a
  // window edge. Inside a modal (any ancestor marked `data-tooltip-bounds`) they are kept within
  // that panel instead, so a bubble over the last button doesn't hang outside the dialog.
  useLayoutEffect(() => {
    if (!isVisible || !clampToViewport || !coords || (placement !== "top" && placement !== "bottom")) {
      setShiftX(0);
      return;
    }
    const width = tooltipRef.current?.getBoundingClientRect().width;
    if (!width) return;
    let minEdge = VIEWPORT_MARGIN;
    let maxEdge = window.innerWidth - VIEWPORT_MARGIN;
    const panelRect = triggerRef.current?.closest("[data-tooltip-bounds]")?.getBoundingClientRect();
    if (panelRect && panelRect.width - 2 * VIEWPORT_MARGIN >= width) {
      minEdge = Math.max(minEdge, panelRect.left + VIEWPORT_MARGIN);
      maxEdge = Math.min(maxEdge, panelRect.right - VIEWPORT_MARGIN);
    }
    const min = minEdge + width / 2;
    const max = maxEdge - width / 2;
    const clamped = Math.min(Math.max(coords.left, min), Math.max(min, max));
    setShiftX(clamped - coords.left);
  }, [isVisible, clampToViewport, coords, placement, fitWidth]);

  const hide = () => {
    setIsVisible(false);
    setCoords(null);
    setFitWidth(null);
    setShiftX(0);
  };

  if (!text && !description && !finalDescription) return children;

  // Premium glassmorphic styles across themes (with light blue background and dark blue border for light theme)
  const colorClasses =
    effectiveTheme === "high-contrast"
      ? "bg-black border-2 border-yellow-300 text-yellow-300 shadow-none"
      : effectiveTheme === "dark"
        ? "bg-slate-900/95 border-slate-800 text-slate-100 shadow-2xl shadow-slate-950/50 backdrop-blur-md"
        : "bg-[#f0f6fe]/95 border-[#236DB4] text-[#236DB4] shadow-xl shadow-blue-900/5 backdrop-blur-md";

  const descriptionColor =
    effectiveTheme === "high-contrast"
      ? "text-yellow-400"
      : effectiveTheme === "dark"
        ? "text-slate-400"
        : "text-[#1e3a8a]";

  const shortcutColor =
    effectiveTheme === "high-contrast"
      ? "border-yellow-300 text-yellow-300"
      : effectiveTheme === "dark"
        ? "bg-slate-800 border-slate-700/60 text-slate-400"
        : "bg-blue-100 border-blue-200 text-[#236DB4]";

  const arrowColorClass =
    effectiveTheme === "high-contrast"
      ? "bg-black border-yellow-300"
      : effectiveTheme === "dark"
        ? "bg-slate-900 border-slate-800"
        : "bg-[#f0f6fe] border-[#236DB4]";

  const positionClasses = {
    top: "-translate-x-1/2 -translate-y-full mb-1",
    bottom: "-translate-x-1/2 mt-1",
    left: "-translate-x-full -translate-y-1/2 mr-1",
    right: "ml-1 -translate-y-1/2",
  };

  // Fixed arrow border alignments to merge perfectly with the container
  const arrowClasses = {
    top: "bottom-[-5px] left-1/2 -translate-x-1/2 border-t-0 border-l-0",
    bottom: "top-[-5px] left-1/2 -translate-x-1/2 border-b-0 border-r-0",
    left: "right-[-5px] top-1/2 -translate-y-1/2 border-b-0 border-l-0",
    right: "left-[-5px] top-1/2 -translate-y-1/2 border-t-0 border-r-0",
  };

  return (
    <>
      <span
        ref={triggerRef}
        data-tooltip-managed="true"
        className={cn("inline-flex cursor-pointer", className)}
        onMouseEnter={() => {
          // Touch screens fire mouseenter on tap and never mouseleave, which would leave the
          // bubble stuck open.
          if (typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches) return;
          setIsVisible(true);
        }}
        onMouseLeave={hide}
        // Dismiss on click: the pointer stays over the trigger afterwards, so the bubble would
        // otherwise sit on top of whatever the click just opened (a flyout, a modal).
        onMouseDown={hide}
        onFocusCapture={hoverOnly ? undefined : () => setIsVisible(true)}
        onBlurCapture={hoverOnly ? undefined : hide}
      >
        {children}
      </span>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={cn(
              "premium-tooltip pointer-events-none fixed z-[9999999] flex flex-col gap-1 rounded-md border px-3 py-1 text-left max-w-xs",
              colorClasses,
              positionClasses[placement] || positionClasses.top,
              tooltipClassName
            )}
            style={{
              top: coords?.top ?? 0,
              left: (coords?.left ?? 0) + shiftX,
              ...(fitWidth ? { width: fitWidth } : {}),
              // Rendered but not painted until the measuring layout effect has run.
              visibility: coords ? "visible" : "hidden",
              fontFamily: "var(--app-font-family, inherit)"
            }}
          >
            {/* Header / Title */}
            {text && (
              <div className="flex items-start justify-between gap-4 font-semibold text-xs leading-snug">
                <span ref={textRef} className="min-w-0 whitespace-normal break-words">{text}</span>
                {shortcut && (
                  <span
                    className={cn(
                      "shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none tracking-widest",
                      shortcutColor
                    )}
                  >
                    {shortcut}
                  </span>
                )}
              </div>
            )}
            {/* Detailed Description */}
            {finalDescription && (
              <p className={cn("text-[11px] font-normal leading-normal whitespace-normal break-words", descriptionColor)}>
                {finalDescription}
              </p>
            )}
            <span
              className={cn(
                "absolute h-2.5 w-2.5 rotate-45 border",
                arrowClasses[placement] || arrowClasses.top,
                arrowColorClass
              )}
              style={shiftX ? { left: `calc(50% - ${shiftX}px)` } : undefined}
              aria-hidden="true"
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default CommonTooltip;